import { computed, Directive, HostListener, input } from '@angular/core';

@Directive({
    selector: '[libSpecialNumberInput]',
    standalone: false
})
export class SpecialNumberInputDirective {

  maxIntegerDigits = input<number | null>(null);
  decimalPoints = input<number>(0);
  maxLength = computed(() => {
    if(this.maxIntegerDigits()) {
      return this.maxIntegerDigits() + this.decimalPoints()    
    } 
     
    return null;
  });

  constructor() { }

  private allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'Clear',
    ',',
  ];

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    let inputValue = (e.target as HTMLInputElement).value;
    if (this.checkAllowedKeys(e)) {
      return;
    }
    if (isNaN(Number(e.key))) {
      e.preventDefault();
    }
    if (this.checkTextSelection()) {
      return;
    }
    if (this.checkForMaxLength(inputValue)) {
      e.preventDefault();
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(e: KeyboardEvent) {
    let inputValue = (e.target as HTMLInputElement).value;
    if (this.containsNonZeros(inputValue)) {
      if (this.maxIntegerDigits() && this.decimalPoints() > 0) {
        this.handleDecimals(e, inputValue, this.maxIntegerDigits())
      } else {
        (e.target as HTMLInputElement).value = this.formatNumber(inputValue);
      }
    } else {
      if(inputValue === '') {
        (e.target as HTMLInputElement).value = '';
      }
      else {
        (e.target as HTMLInputElement).value = '0';
      }
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(e: Event) {
    let inputValue = (e.target as HTMLInputElement).value;
    if (this.decimalPoints() > 0) {
      if (this.containsNonZeros(inputValue)) {
        (e.target as HTMLInputElement).value = this.formatNumber(inputValue);
      } else {
        (e.target as HTMLInputElement).value = '';
      }
    }
  }

  private containsNonZeros(inputValue: string) {
    return inputValue.split('').some(i => i !== '0');
  }

  private checkTextSelection() {
    return window.getSelection()!.toString().trim().length > 0;
  }

  private checkAllowedKeys(e: KeyboardEvent): boolean {
    const allKeys = [
      ...this.allowedKeys,
      this.decimalPoints() > 0 ? '.' : '',
    ];

    return (allKeys.indexOf(e.key) > -1 || (e.key === 'a' && e.ctrlKey) || (e.key === 'a' && e.metaKey));
  }

  private checkForMaxLength(inputValue: string): boolean {
    if (this.decimalPoints() > 0) {
      if(inputValue.includes('.')){
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxLength()) {
          return true;
        }
      }
      else {
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxIntegerDigits()) {
          return true;
        }
      }
    } else {
      if (inputValue.replace(/\,/g, '').length === this.maxLength()) {
        return true;
      }
    }
    return false;
  }

  private handleDecimals(e: KeyboardEvent, inputValue: string, maxIntegerDigits?: number) {
    if (!this.checkIfTryingToDelete(e)) {
      (e.target as HTMLInputElement).value = this.removeExtraDecimals(inputValue, maxIntegerDigits)
    } else return
  }

  private removeExtraDecimals(inputValue: string, maxIntegerDigits?: number): string {
    if(inputValue.includes('.') && inputValue.indexOf('.') !== inputValue.lastIndexOf('.')){
      return inputValue.slice(0, inputValue.lastIndexOf('.'));
    }
    return inputValue;
  }

  private checkIfTryingToDelete(e: KeyboardEvent): boolean {
    return this.checkTextSelection() || e.key === 'Backspace' || e.key === 'Delete';
  }

  private formatNumber(inputValue: string): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: this.decimalPoints(),
      maximumFractionDigits: this.decimalPoints(),
    }).format(+inputValue.replace(/\,/g, ''));
  }

};
